// Backend-mediated ScaiKey OIDC. The SPA holds no client_id, audience, tenant
// slug, or PKCE verifier — every step goes through /v1/auth/* on its own
// backend, which talks to ScaiKey using credentials the SPA never sees.
//
// Token storage:
// - access_token: sessionStorage (cleared on tab close)
// - refresh_token: localStorage so a returning user doesn't need to re-login.
//   Opaque from the SPA's POV — the backend validates it against ScaiKey on
//   every refresh.

import type { Accessor } from "solid-js";
import { createSignal } from "solid-js";

type TokenSet = {
  access_token: string;
  refresh_token: string | null;
  expires_at: number; // unix seconds
};

export type Tenant = { id: string; name: string; slug: string };
export type AuthUser = { id: string; email: string; name: string };

const ACCESS_KEY = "scaicrm.auth.access";
const REFRESH_KEY = "scaicrm.auth.refresh";
const RETURN_TO_KEY = "scaicrm.auth.return_to";

const [tokens, setTokens] = createSignal<TokenSet | null>(loadTokens());

export const accessToken: Accessor<string | null> = () => tokens()?.access_token ?? null;
export const isAuthenticated: Accessor<boolean> = () => !!tokens();

function loadTokens(): TokenSet | null {
  try {
    const raw = sessionStorage.getItem(ACCESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TokenSet;
    if (parsed.expires_at <= Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(ACCESS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistTokens(t: {
  access_token: string;
  refresh_token?: string | null;
  expires_at: number;
}): void {
  const refresh = t.refresh_token ?? localStorage.getItem(REFRESH_KEY);
  const set: TokenSet = {
    access_token: t.access_token,
    refresh_token: refresh,
    expires_at: t.expires_at,
  };
  sessionStorage.setItem(ACCESS_KEY, JSON.stringify(set));
  if (t.refresh_token) {
    localStorage.setItem(REFRESH_KEY, t.refresh_token);
  }
  setTokens(set);
}

function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  setTokens(null);
}

// ── Flow ──────────────────────────────────────────────────────────────────────

/** Start the login flow: ask the backend for a ScaiKey authorize URL, then go. */
export async function startLogin(returnTo: string = "/"): Promise<void> {
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  const resp = await fetch("/v1/auth/authorize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ return_origin: window.location.origin }),
  });
  if (!resp.ok) {
    throw new Error(`authorize failed: ${resp.status} ${await resp.text()}`);
  }
  const { authorize_url } = (await resp.json()) as { authorize_url: string };
  window.location.assign(authorize_url);
}

export type TokenExchangeResult =
  | { requires_tenant_selection: true; tenants: Tenant[]; session_code: string }
  | {
      requires_tenant_selection?: false;
      access_token: string;
      expires_at: number;
      refresh_token?: string | null;
      user: AuthUser;
      tenant: Tenant;
    };

/**
 * Hand the backend a session_code, optionally with the tenant_id the user
 * picked. Returns either a tenant picker (multi-tenant case) or the final
 * token + user + tenant info (single-tenant or post-pick).
 */
export async function exchangeSessionCode(
  sessionCode: string,
  tenantId?: string,
): Promise<TokenExchangeResult> {
  const resp = await fetch("/v1/auth/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_code: sessionCode,
      tenant_id: tenantId ?? null,
    }),
  });
  if (!resp.ok) {
    throw new Error(`token exchange failed: ${resp.status} ${await resp.text()}`);
  }
  const data = await resp.json();
  if (data.requires_tenant_selection) {
    return {
      requires_tenant_selection: true,
      tenants: data.tenants as Tenant[],
      session_code: sessionCode,
    };
  }
  persistTokens({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: data.expires_at,
  });
  return {
    requires_tenant_selection: false,
    access_token: data.access_token,
    expires_at: data.expires_at,
    refresh_token: data.refresh_token,
    user: data.user,
    tenant: data.tenant,
  };
}

/** Where the user came from before login, set by `startLogin`. */
export function consumeReturnTo(): string {
  const r = sessionStorage.getItem(RETURN_TO_KEY) ?? "/";
  sessionStorage.removeItem(RETURN_TO_KEY);
  return r;
}

let refreshInFlight: Promise<string | null> | null = null;

export async function refreshIfNeeded(): Promise<string | null> {
  const t = tokens();
  if (!t) return null;
  if (t.expires_at - 60 > Math.floor(Date.now() / 1000)) return t.access_token;
  if (!t.refresh_token) {
    clearTokens();
    return null;
  }
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const resp = await fetch("/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: t.refresh_token }),
      });
      if (!resp.ok) {
        clearTokens();
        return null;
      }
      const data = await resp.json();
      persistTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
      });
      return data.access_token as string;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function logout(): void {
  clearTokens();
  window.location.assign("/login");
}
